// Legacy-compatibility banner + migration UI. When a strategy still contains quarantined legacy
// `data.code` blocks (retired JavaScript that no longer runs), this:
//   1. classifies them (exact canonical / convertible-custom / manual-review),
//   2. offers "Migrate N canonical blocks automatically" — a server-faithful, gate-derived rewrite to the
//      trusted-TS `__canonical` marker (params/connections/labels/layout preserved, code removed, provenance
//      stamped) shown in a preview first, applied to the WORKING COPY (persisted only when the user saves),
//      with one-click Undo;
//   3. offers "Review M custom blocks in TSL Studio" — opens the Studio pre-filled with a best-effort TSL
//      conversion the user validates (parse/type/gate/no-lookahead/preview) before accepting;
//   4. offers "View migration report".
// Never executes the legacy JavaScript. See ADR 0001 / docs/STRATEGY_PAYLOAD_CONTRACT.md.

import React, { useEffect, useMemo, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { AlertTriangle, Wand2, Code2, FileText, RotateCcw, CheckCircle2, X } from 'lucide-react';
import { useStrategy } from '../../contexts/StrategyContext';
import type { Block } from '../../types';
import { classifyStrategy, applyCanonicalMigration, type ClassifiedBlock } from '../../engine/migration/migrateLegacyStrategy';
import { convertCustomToTsl } from '../../engine/migration/customTslConversion';
import { TslBlockStudio } from './TslBlockStudio';

/** True if a custom block carries non-empty `data.code` and has no TSL AST (quarantined legacy). */
// Genuinely QUARANTINED (no-longer-running) custom blocks: carry executable `data.code` but have NEITHER a
// TSL AST NOR a `__canonical` marker. A block with a `__canonical` marker still RUNS — the engine dispatches
// it to trusted TS (riskRules / qtyCalculator) and ignores the vestigial code — so it is NOT "no longer run"
// (it just carries retired code worth cleaning up; see legacyCodeBearingBlocks).
export function legacyCodeBlockNames(blocks: Array<{ type?: string; data?: unknown }>): string[] {
  const out: string[] = [];
  for (const b of blocks) {
    if (b?.type !== 'custom') continue;
    const d = (b.data ?? {}) as { name?: string; code?: string; tslAst?: unknown; parameters?: { __canonical?: unknown } };
    const code = typeof d.code === 'string' ? d.code.trim() : '';
    const hasAst = d.tslAst != null && typeof d.tslAst === 'object';
    const hasMarker = typeof d.parameters?.__canonical === 'string';
    if (code.length > 0 && !hasAst && !hasMarker) out.push(d.name ?? 'custom block');
  }
  return out;
}

/** Any custom block still carrying executable `data.code` (dead OR marker-dispatched vestigial). Used to
 *  decide whether the migration banner should appear at all (there is retired code to clean up / convert). */
export function legacyCodeBearingBlocks(blocks: Array<{ type?: string; data?: unknown }>): number {
  return blocks.filter((b) => b?.type === 'custom' && typeof (b.data as { code?: unknown })?.code === 'string' && (b.data as { code: string }).code.trim().length > 0 && !((b.data as { tslAst?: unknown }).tslAst)).length;
}

type Modal = null | 'preview' | 'custom' | 'report';

export const LegacyCodeBlockBanner: React.FC<{ suppressed?: boolean }> = ({ suppressed }) => {
  const { blocks, patchBlocks } = useStrategy();
  const [items, setItems] = useState<ClassifiedBlock[] | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  const [snapshot, setSnapshot] = useState<Block[] | null>(null);   // pre-migration blocks → Undo
  const [justRecovered, setJustRecovered] = useState<number | null>(null);
  const [studioText, setStudioText] = useState<string | null>(null);
  useEscapeKey(() => setModal(null), modal !== null);  // Esc closes the open migration modal

  // (Re)classify whenever the strategy's legacy footprint changes.
  const codeBearing = useMemo(() => legacyCodeBearingBlocks(blocks as Array<{ type?: string; data?: unknown }>), [blocks]);
  useEffect(() => {
    let alive = true;
    classifyStrategy(blocks as never).then((r) => { if (alive) setItems(r.items); });
    return () => { alive = false; };
  }, [codeBearing]); // eslint-disable-line react-hooks/exhaustive-deps

  const canonical = (items ?? []).filter((i) => i.classification === 'canonical-exact');  // run via marker; carry vestigial code
  const custom = (items ?? []).filter((i) => i.classification === 'convertible-custom');
  const manual = (items ?? []).filter((i) => i.classification === 'manual-review');
  // genuinely NOT running = code, no AST, no canonical marker (the engine can't dispatch these).
  const dead = legacyCodeBlockNames(blocks as Array<{ type?: string; data?: unknown }>).length;
  const quarantined = custom.length + manual.length;
  // `notLegacy` is the CURRENT not-legacy count. After a recovery it INCLUDES the just-recovered blocks
  // (they now carry a marker + no code), so it must NOT be reported alongside "N recovered" without
  // subtracting them — else recovered + quarantined + notLegacy double-counts the recovered ones (the
  // "41 vs 34" bug). The honest partition of the strategy is: recovered + quarantined + alreadyNative = total.
  const notLegacy = (items ?? []).filter((i) => i.classification === 'not-legacy').length;
  const alreadyNative = Math.max(0, notLegacy - (justRecovered ?? 0));
  const totalBlocks = notLegacy + quarantined;

  // Suppressed while the builder is still RESOLVING a persisted templateId (lifecycle redirect pending) —
  // the retired payload must never render, not even for a frame, while we may be about to swap in the
  // migrated successor. (Hooks above always run; this guard is after them.)
  if (suppressed) return null;
  if (codeBearing === 0 && justRecovered == null) return null;

  const applyCanonical = async () => {
    const orig = blocks.map((b) => JSON.parse(JSON.stringify(b))) as Block[];
    const { blocks: migrated, report } = await applyCanonicalMigration(blocks as never, { nowIso: new Date().toISOString(), migrationVersion: '1' });
    setSnapshot(orig);
    patchBlocks(migrated as Block[]);
    setJustRecovered(report.recovered);
    setModal(null);
  };
  const undo = () => { if (snapshot) { patchBlocks(snapshot); setSnapshot(null); setJustRecovered(null); } };

  const customConv = (it: ClassifiedBlock) => {
    const block = blocks.find((b) => (b as { id?: string }).id === it.id) as { data?: { parameters?: Record<string, unknown> } } | undefined;
    return convertCustomToTsl(it.conversionTarget ?? '', (block?.data?.parameters ?? {}) as Record<string, unknown>);
  };

  return (
    <div className="text-xs text-red-900 bg-red-50 border border-red-200 rounded p-2 space-y-2">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="flex-1">
          {justRecovered != null ? (
            <>
              <b>{justRecovered} canonical block{justRecovered === 1 ? '' : 's'} recovered</b> (now running via their trusted reference).{' '}
              {quarantined > 0
                ? <>{quarantined} custom block{quarantined === 1 ? '' : 's'} still quarantined — the strategy stays partially disabled until {quarantined === 1 ? 'it is' : 'they are'} migrated. {alreadyNative} already native · {totalBlocks} blocks total.</>
                : <>All legacy blocks recovered · {totalBlocks} blocks total.</>}
              <span className="block mt-0.5 text-red-700">Review the changes, then <b>Save</b> / <b>Update template</b> to persist. Nothing is saved automatically.</span>
            </>
          ) : (
            <>
              <b>Migration available:</b>{' '}
              {dead > 0 && <><b>{dead} custom block{dead === 1 ? '' : 's'}</b> {dead === 1 ? 'is' : 'are'} written in the retired JavaScript format and <b>no longer run</b>{custom.length > 0 ? ' — re-author as TSL' : ''}. </>}
              {canonical.length > 0 && <>{canonical.length} canonical block{canonical.length === 1 ? '' : 's'} still {canonical.length === 1 ? 'runs' : 'run'} via {canonical.length === 1 ? 'its' : 'their'} trusted marker but carr{canonical.length === 1 ? 'ies' : 'y'} retired code — clean {canonical.length === 1 ? 'it' : 'them'} up automatically. </>}
              {manual.length > 0 && <>{manual.length} need manual review.</>}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {justRecovered != null && snapshot && (
          <button onClick={undo} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100">
            <RotateCcw className="w-3.5 h-3.5" /> Undo migration
          </button>
        )}
        {justRecovered == null && canonical.length > 0 && (
          <button onClick={() => setModal('preview')} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">
            <Wand2 className="w-3.5 h-3.5" /> Migrate {canonical.length} canonical block{canonical.length === 1 ? '' : 's'} automatically
          </button>
        )}
        {custom.length > 0 && (
          <button onClick={() => setModal('custom')} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
            <Code2 className="w-3.5 h-3.5" /> Review {custom.length} custom block{custom.length === 1 ? '' : 's'} in TSL Studio
          </button>
        )}
        {items && (items.some((i) => i.classification !== 'not-legacy')) && (
          <button onClick={() => setModal('report')} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50">
            <FileText className="w-3.5 h-3.5" /> View migration report
          </button>
        )}
      </div>

      {/* Preview before applying the canonical migration */}
      {modal === 'preview' && (
        <Sheet title="Migrate canonical blocks — preview" onClose={() => setModal(null)}>
          <p className="text-xs text-gray-600 mb-2">Each block below exactly matches a trusted built-in. Migrating stamps the canonical reference, preserves your parameters &amp; connections, and removes the retired code. Applied to your working copy only — save to persist.</p>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500 border-b"><th className="py-1 pr-2">Legacy block</th><th className="py-1 pr-2">Canonical reference</th><th className="py-1 pr-2">Params kept</th><th className="py-1">Match</th></tr></thead>
            <tbody>
              {canonical.map((c) => (
                <tr key={c.id} className="border-b last:border-0 align-top">
                  <td className="py-1 pr-2 font-medium text-gray-800">{c.name}</td>
                  <td className="py-1 pr-2 font-mono text-emerald-700">{c.canonicalId}<span className="text-gray-400"> v{c.canonicalVersion}</span></td>
                  <td className="py-1 pr-2 text-gray-500">{c.paramsPreserved.length}</td>
                  <td className="py-1 text-gray-500" title={c.reason}>{c.confidence}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={applyCanonical} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4" /> Migrate {canonical.length} block{canonical.length === 1 ? '' : 's'}</button>
            <button onClick={() => setModal(null)} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </Sheet>
      )}

      {/* Guided custom conversion */}
      {modal === 'custom' && (
        <Sheet title="Convert custom blocks to TSL" onClose={() => setModal(null)}>
          <p className="text-xs text-gray-600 mb-2">These blocks hold genuinely custom logic. Open one in the TSL Studio with a best-effort conversion pre-filled — validate (parse / gate / no-lookahead / preview) and adjust before accepting. The legacy JavaScript is never run.</p>
          <ul className="space-y-1.5">
            {custom.map((c) => (
              <li key={c.id} className="flex items-center justify-between border-b last:border-0 py-1">
                <span className="text-gray-800">{c.name} <span className="text-[10px] text-gray-400">({c.conversionTarget})</span></span>
                <button onClick={() => { const conv = customConv(c); if (conv) { setStudioText(conv.tslText); setModal(null); } }} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border border-gray-300 text-gray-700 hover:bg-gray-50"><Code2 className="w-3 h-3" /> Open in Studio</button>
              </li>
            ))}
          </ul>
        </Sheet>
      )}

      {/* Full migration report */}
      {modal === 'report' && items && (
        <Sheet title="Migration report" onClose={() => setModal(null)}>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500 border-b"><th className="py-1 pr-2">Block</th><th className="py-1 pr-2">Type</th><th className="py-1 pr-2">Classification</th><th className="py-1">Detail</th></tr></thead>
            <tbody>
              {items.filter((i) => i.classification !== 'not-legacy').map((i) => (
                <tr key={i.id} className="border-b last:border-0 align-top">
                  <td className="py-1 pr-2 font-medium text-gray-800">{i.name}</td>
                  <td className="py-1 pr-2 text-gray-500">{i.blockType}</td>
                  <td className="py-1 pr-2">{i.classification === 'canonical-exact' ? <span className="text-emerald-700">{i.canonicalId}</span> : i.classification}</td>
                  <td className="py-1 text-gray-500">{i.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Sheet>
      )}

      {studioText != null && <TslBlockStudio initialText={studioText} onClose={() => setStudioText(null)} />}
    </div>
  );
};

const Sheet: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3"><div className="text-sm font-semibold text-gray-800">{title}</div><button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button></div>
      {children}
    </div>
  </div>
);

export default LegacyCodeBlockBanner;
